import { Component, ComponentType } from 'react'
import * as omit from 'lodash/omit'
import * as debounce from 'lodash/debounce'
import { FlagsProps, LoadersProps } from 'interfaces/guards'
import { checkGuards, checkIfLoaded } from 'helpers/loadableHelpers'
import { GUARD_LOADING_TIMEOUT } from 'constants/component'

export type LoadableStateProps = FlagsProps & LoadersProps

type LoadableComponentsProps<TComponentsProps> = {
  content: ComponentType<TComponentsProps>
  preloader: ComponentType<TComponentsProps> | false
}

export type LoadableOwnProps<TComponentsProps> =
  & TComponentsProps
  & LoadableComponentsProps<TComponentsProps>

export type LoadableProps<TComponentsProps> =
  & LoadableStateProps
  & LoadableOwnProps<TComponentsProps>

export const OMITABLE_PROP_NAMES: (keyof LoadableStateProps | keyof LoadableComponentsProps<any>)[] = [
  'flags', 'loaders', 'content', 'preloader', 'loaderNames',
]

type LoadableState<TComponentsProps> = {
  isLoaded: boolean
  lastComponentsProps: TComponentsProps | null
}

const isDataLoaded = (props: Readonly<LoadableProps<any>>) => {
  return props.flags.every(Boolean)
}

const getComponentsProps = <TComponentsProps extends {}>(props: LoadableProps<TComponentsProps>): TComponentsProps => {
  return omit(props, OMITABLE_PROP_NAMES) as TComponentsProps
}

class Loadable<TComponentsProps extends {}> extends Component<LoadableProps<TComponentsProps>, LoadableState<TComponentsProps>> {
  static getDerivedStateFromProps (nextProps: LoadableProps<any>, prevState: LoadableState<any>) {
    const isLoaded = isDataLoaded(nextProps)

    return {
      isLoaded,
      lastComponentsProps: isLoaded
        ? getComponentsProps(nextProps)
        : prevState.lastComponentsProps,
    }
  }

  constructor (props: Readonly<LoadableProps<TComponentsProps>>, context?: any) {
    super(props as any, context)

    const isLoaded = isDataLoaded(props)
    this.state = {
      isLoaded,
      lastComponentsProps: isLoaded || props.preloader === false
        ? getComponentsProps(props)
        : null,
    }
  }

  render () {
    // It is TS bug, I should declare types explicitly
    const Content: ComponentType<TComponentsProps> = this.props.content
    const Preloader: ComponentType<TComponentsProps> | false = this.props.preloader

    if ((!this.state.isLoaded && this.props.preloader !== false) || !this.state.lastComponentsProps) {
      if (!Preloader) {
        // This scenario never happens
        return null
      }

      return (
        <Preloader { ...getComponentsProps(this.props) } />
      )
    }

    return (
      <Content { ...this.state.lastComponentsProps } />
    )
  }

  componentDidMount () {
    const { flags, loaders } = this.props
    checkGuards(flags, loaders)

    const { isLoaded } = this.state
    if (!isLoaded) {
      this.startLoadingChecking()
    }
  }

  componentDidUpdate () {
    const { isLoaded } = this.state

    if (!isLoaded) {
      const { flags, loaders } = this.props
      checkGuards(flags, loaders)
    }

    // restart checking each time
    this.startLoadingChecking.cancel()

    if (!isLoaded) {
      this.startLoadingChecking()
    }
  }

  componentWillUnmount () {
    this.startLoadingChecking.cancel()
  }

  private checkIfLoaded = () => {
    const { flags, loaderNames, content } = this.props
    return checkIfLoaded(this.state.isLoaded, flags, loaderNames, content)
  }

  private startLoadingChecking = debounce(this.checkIfLoaded, GUARD_LOADING_TIMEOUT)
}


export default Loadable
